import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HiArrowUpRight } from "react-icons/hi2";
import api from "../../lib/api";
import ImageUploader from "./ImageUploader";

export function ShopifyList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/shopify/admin");
      setItems(data.items || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    await api.delete(`/api/shopify/admin/${s.id}`);
    load();
  };

  const togglePublish = async (s) => {
    const next = s.status === "published" ? "draft" : "published";
    await api.patch(`/api/shopify/admin/${s.id}`, { status: next });
    load();
  };

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="eyebrow">Studio</span>
          <h1 style={{ marginTop: 8 }}>Shopify portfolio</h1>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {["all", "draft", "published"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className="chip"
              style={{
                cursor: "pointer",
                background: filter === f ? "var(--accent)" : "transparent",
                color: filter === f ? "#0a0a0a" : "var(--text-soft)",
                borderColor: filter === f ? "var(--accent)" : "var(--border-strong)",
                textTransform: "capitalize",
              }}
            >
              {f}
            </button>
          ))}
          <button type="button" className="btn" onClick={() => nav("/admin/shopify/new")}>
            New entry <span className="arrow"><HiArrowUpRight /></span>
          </button>
        </div>
      </div>

      {loading && <p className="text-muted">Loading…</p>}
      {error && <p style={{ color: "var(--danger)" }}>{error}</p>}

      {!loading && !error && (() => {
        const filtered = items.filter((s) => filter === "all" || (s.status || "draft") === filter);
        return (
        <div className="admin-card" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 36, textAlign: "center", color: "var(--muted)" }}>
              {items.length === 0 ? "No entries yet." : `No entries with status "${filter}".`}
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>URL</th>
                  <th>Order</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id}>
                    <td><Link to={`/admin/shopify/${s.id}`}>{s.title}</Link></td>
                    <td><span className={`status-pill ${s.status === "published" ? "published" : "draft"}`}>{s.status || "draft"}</span></td>
                    <td style={{ fontSize: 13, color: "var(--muted)" }}>{s.url}</td>
                    <td style={{ color: "var(--muted)", fontSize: 13 }}>{s.order ?? 0}</td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 8 }}>
                        <button type="button" className="chip" style={{ cursor: "pointer" }} onClick={() => togglePublish(s)}>
                          {s.status === "published" ? "Unpublish" : "Publish"}
                        </button>
                        <Link className="chip" to={`/admin/shopify/${s.id}`}>Edit</Link>
                        <a className="chip" href={s.url} target="_blank" rel="noreferrer noopener">Visit</a>
                        <button type="button" className="chip" style={{ cursor: "pointer", color: "var(--danger)", borderColor: "rgba(255,107,107,0.3)" }} onClick={() => remove(s)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );})()}
    </div>
  );
}

const EMPTY = { title: "", url: "", image: "", category: "Shopify", note: "", order: 0, slug: "", status: "draft" };

export function ShopifyEditor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [item, setItem] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    let live = true;
    api.get(`/api/shopify/admin/${id}`)
      .then(({ data }) => { if (live) setItem({ ...EMPTY, ...data.item }); })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, isNew]);

  const set = (k, v) => setItem((p) => ({ ...p, [k]: v }));

  const persist = async (overrides = {}) => {
    if (!item.title) { setError("Title required."); return; }
    setBusy(true); setError(null);
    try {
      const body = { ...item, ...overrides };
      if (isNew) {
        const { data } = await api.post("/api/shopify/admin", body);
        nav(`/admin/shopify/${data.item.id}`, { replace: true });
      } else {
        const { data } = await api.patch(`/api/shopify/admin/${id}`, body);
        setItem({ ...EMPTY, ...data.item });
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const save = () => persist();
  const publish = () => persist({ status: "published" });
  const unpublish = () => persist({ status: "draft" });

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="eyebrow">{isNew ? "New" : "Edit"} Shopify entry</span>
          <h1 style={{ marginTop: 8 }}>{item.title || "Untitled"}</h1>
          {!isNew && (
            <span className={`status-pill ${item.status === "published" ? "published" : "draft"}`} style={{ marginTop: 8 }}>
              {item.status || "draft"}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link className="btn btn--ghost" to="/admin/shopify">Back</Link>
          <button type="button" className="btn btn--ghost" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save draft"}
          </button>
          {item.status === "published" ? (
            <button type="button" className="btn btn--ghost" onClick={unpublish} disabled={busy}>
              Unpublish
            </button>
          ) : (
            <button type="button" className="btn" onClick={publish} disabled={busy}>
              {isNew ? "Save & publish" : "Publish"}
            </button>
          )}
        </div>
      </div>

      {error && <p style={{ color: "var(--danger)" }}>{error}</p>}

      <div className="admin-card" style={{ maxWidth: 880 }}>
        <div className="row">
          <div className="field">
            <label>Title</label>
            <input className="input" value={item.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="field">
            <label>Slug</label>
            <input className="input" value={item.slug || ""} placeholder="auto-from-title" onChange={(e) => set("slug", e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>URL</label>
          <input className="input" value={item.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" />
        </div>
        <div className="row">
          <div className="field">
            <label>Category</label>
            <input className="input" value={item.category} onChange={(e) => set("category", e.target.value)} placeholder="Shopify" />
          </div>
          <div className="field">
            <label>Note (optional)</label>
            <input className="input" value={item.note} onChange={(e) => set("note", e.target.value)} placeholder="One-line context" />
          </div>
        </div>

        <ImageUploader
          label="Storefront screenshot"
          value={item.image}
          onChange={(u) => set("image", u)}
          aiEnabled
          aiContext={{ title: item.title, excerpt: item.note, tags: [item.category || "Shopify"] }}
        />

        <div className="field">
          <label>Order (lower = first)</label>
          <input className="input" type="number" value={item.order} onChange={(e) => set("order", e.target.value)} />
        </div>
      </div>
    </div>
  );
}
